import { type DeepReadonly } from '../typeUtils'

enum ChyronTypeDTO {
  Speaker = 'speaker',
  SpeakerInfo = 'speaker_info',
  SpeakersOverview = 'speakers_overview',
  ComingUp = 'coming_up',
  Question = 'question',
  PollResult = 'poll',
  WebinarInfo = 'webinar_info',
  J2SIntroduction = 'j2s_intro',
  CornerTag = 'cornertag',
}

enum ChyronType {
  Speaker = 'speaker',
  SpeakerInfo = 'speaker_info',
  ComingUp = 'coming_up',
  SpeakersOverview = 'speakers_overview',
  Question = 'question',
  NextUp = 'next_up',
  PollResult = 'poll',
  WebinarInfo = 'webinar_info',
  J2SIntroduction = 'j2s_intro',
  CornerTag = 'cornertag',
}

interface SpeakerChyronDTO {
  type: ChyronTypeDTO.Speaker
  data: {
    name: string
    title?: string
    avatarUrl?: string
  }
}

interface SpeakerChyron {
  type: ChyronType.Speaker
  name: string
  title?: string
  avatarUrl?: string
}

interface SpeakerInfoChyron {
  type: ChyronType.SpeakerInfo
  liveSpeakerId: string
  name: string
  title?: string
  avatarUrl?: string
  bio?: string
  company?: string
}

interface SpeakerInfoChyronDTO {
  type: ChyronTypeDTO.SpeakerInfo
  data: {
    liveSpeakerId: string
    name: string
    title?: string
    avatarUrl?: string
    bio?: string
    company?: string
  }
}

interface SpeakersOverviewChyron {
  type: ChyronType.SpeakersOverview
  speakers: {
    name: string
    title?: string
    avatarUrl?: string
    company?: string
  }[]
}

interface SpeakersOverviewChyronDTO {
  type: ChyronTypeDTO.SpeakersOverview
  data?: {
    speakers: {
      name: string
      title?: string
      avatarUrl?: string
    }[]
  }
}

interface QuestionChyronDTO {
  type: ChyronTypeDTO.Question
  data: {
    id: number
    name: string
    text: string
  }
}

interface QuestionChyron {
  type: ChyronType.Question
  id: number
  question: string
  name: string
  title?: string
  avatarUrl?: string
}

interface NextUpChyron {
  type: ChyronType.NextUp
  name: string
  title?: string
}

interface PollResultChyronDTO {
  type: ChyronTypeDTO.PollResult
  data: {
    answer_count: string
    isOpen: boolean
    opened_date: string
    id: string
    question: string
    options: {
      id: number
      option: string
      answerPercentage: number
    }[]
  }
}

interface PollResultChyron {
  type: ChyronType.PollResult
  id: string
  question: string
  isOpen: boolean
  options: readonly {
    id: number
    option: string
    answerPercentage: number
  }[]
}

interface WebinarInfoChyronDTO {
  type: ChyronTypeDTO.WebinarInfo
  data: {
    title: string
    date: number // unix timestamp in milliseconds
    locale: string
    thumbnail: string
  }
}

interface WebinarInfoChyron {
  type: ChyronType.WebinarInfo
  title: string
  date: Date
  locale: string
  thumbnail: string
}

interface ComingUpChyron {
  cardId: string
  type: ChyronType.ComingUp
  text: string
}

interface ComingUpChyronDTO {
  type: ChyronTypeDTO.ComingUp
  data: {
    cardId: string
    text: string
  }
}

interface ChyronCornerTagDTO {
  type: ChyronTypeDTO.CornerTag
  data: {
    text: string
  }
}

interface CornerTagChyron {
  type: ChyronType.CornerTag
  text: string
}

interface J2SIntroChyronDTO {
  type: ChyronTypeDTO.J2SIntroduction
}

interface J2SIntroChyron {
  type: ChyronType.J2SIntroduction
}

type ChyronDTO =
  | SpeakerChyronDTO
  | PollResultChyronDTO
  | QuestionChyronDTO
  | SpeakerInfoChyronDTO
  | SpeakersOverviewChyronDTO
  | WebinarInfoChyronDTO
  | J2SIntroChyronDTO
  | ComingUpChyronDTO
  | ChyronCornerTagDTO

type Chyron =
  | SpeakerChyron
  | NextUpChyron
  | QuestionChyron
  | PollResultChyron
  | SpeakerInfoChyron
  | SpeakersOverviewChyron
  | WebinarInfoChyron
  | J2SIntroChyron
  | ComingUpChyron
  | CornerTagChyron

export type {
  Chyron,
  NextUpChyron,
  PollResultChyron,
  QuestionChyron,
  ComingUpChyron,
  SpeakerChyron,
  SpeakerInfoChyron,
  SpeakersOverviewChyron,
  WebinarInfoChyron,
  J2SIntroChyron,
  CornerTagChyron,
}

export type {
  SpeakersOverviewChyronDTO,
  SpeakerChyronDTO,
  SpeakerInfoChyronDTO,
  ChyronDTO,
  QuestionChyronDTO,
  PollResultChyronDTO,
  WebinarInfoChyronDTO,
  ComingUpChyronDTO,
  J2SIntroChyronDTO,
  ChyronCornerTagDTO,
}

function mapDTOToSpeakerChyron(
  speakerChyronDTO: SpeakerChyronDTO
): SpeakerChyron {
  return {
    type: ChyronType.Speaker,
    name: speakerChyronDTO.data.name,
    title: speakerChyronDTO.data.title,
  }
}

function mapDTOToSpeakerInfoChyron(
  speakerInfoChyronDTO: SpeakerInfoChyronDTO
): SpeakerInfoChyron {
  return {
    type: ChyronType.SpeakerInfo,
    liveSpeakerId: speakerInfoChyronDTO.data.liveSpeakerId,
    name: speakerInfoChyronDTO.data.name,
    title: speakerInfoChyronDTO.data.title,
    avatarUrl: speakerInfoChyronDTO.data.avatarUrl,
    bio: speakerInfoChyronDTO.data.bio,
    company: speakerInfoChyronDTO.data.company,
  }
}

function mapDTOToSpeakersOverviewChyron(
  speakersOverviewDTO: DeepReadonly<SpeakersOverviewChyronDTO>
): SpeakersOverviewChyron {
  return {
    type: ChyronType.SpeakersOverview,
    speakers: [...(speakersOverviewDTO.data?.speakers ?? [])],
  }
}

function mapDTOToQuestionChyron(
  questionChyronDTO: QuestionChyronDTO
): QuestionChyron {
  return {
    type: ChyronType.Question,
    id: questionChyronDTO.data.id,
    name: questionChyronDTO.data.name,
    question: questionChyronDTO.data.text,
  }
}

function mapDTOToPollResultChyron(
  pollResultChyronDTO: DeepReadonly<PollResultChyronDTO>
): PollResultChyron {
  return {
    type: ChyronType.PollResult,
    id: pollResultChyronDTO.data.id,
    isOpen: pollResultChyronDTO.data.isOpen,
    question: pollResultChyronDTO.data.question,
    options: pollResultChyronDTO.data.options.map((option) => ({
      id: option.id,
      option: option.option,
      answerPercentage: option.answerPercentage,
    })),
  }
}

function mapDTOToWebinarInfoChyron(
  chyronDTO: WebinarInfoChyronDTO
): WebinarInfoChyron {
  return {
    type: ChyronType.WebinarInfo,
    title: chyronDTO.data.title,
    date: new Date(chyronDTO.data.date),
    locale: chyronDTO.data.locale,
    thumbnail: chyronDTO.data.thumbnail,
  }
}

function mapDTOtoComingUpChyron(
  comingUpChyronDTO: ComingUpChyronDTO
): ComingUpChyron {
  return {
    cardId: comingUpChyronDTO.data.cardId,
    type: ChyronType.ComingUp,
    text: comingUpChyronDTO.data.text,
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function mapDTOToJ2SIntroChyron(chyronDTO: J2SIntroChyronDTO): J2SIntroChyron {
  return {
    type: ChyronType.J2SIntroduction,
  }
}

function mapDTOToCornerTagChyron(
  chyronDTO: ChyronCornerTagDTO
): CornerTagChyron {
  return {
    type: ChyronType.CornerTag,
    text: chyronDTO.data.text,
  }
}

// This is an exhaustive switch statement. Disabling because our eslint
// configuration is not thought through for TypeScript.
// https://twentythreecom.atlassian.net/browse/BAS-1064
// eslint-disable-next-line consistent-return
function mapDTOToChyron(chyronDTO: DeepReadonly<ChyronDTO>) {
  // eslint-disable-next-line default-case
  switch (chyronDTO.type) {
    case ChyronTypeDTO.Speaker: {
      return mapDTOToSpeakerChyron(chyronDTO)
    }
    case ChyronTypeDTO.ComingUp: {
      return mapDTOtoComingUpChyron(chyronDTO)
    }
    case ChyronTypeDTO.PollResult: {
      return mapDTOToPollResultChyron(chyronDTO)
    }
    case ChyronTypeDTO.Question: {
      return mapDTOToQuestionChyron(chyronDTO)
    }
    case ChyronTypeDTO.SpeakerInfo: {
      return mapDTOToSpeakerInfoChyron(chyronDTO)
    }
    case ChyronTypeDTO.SpeakersOverview: {
      return mapDTOToSpeakersOverviewChyron(chyronDTO)
    }
    case ChyronTypeDTO.WebinarInfo: {
      return mapDTOToWebinarInfoChyron(chyronDTO)
    }
    case ChyronTypeDTO.J2SIntroduction: {
      return mapDTOToJ2SIntroChyron(chyronDTO)
    }
    case ChyronTypeDTO.CornerTag: {
      return mapDTOToCornerTagChyron(chyronDTO)
    }
  }
  return null
}

export { ChyronType, ChyronTypeDTO, mapDTOToChyron }
